import { getCookie, setCookie, clearAllCookies } from "../../services/Cookies";

const BASE_URL = process.env.REACT_APP_API_URL ?? "";
const REFRESH_PATH = "/token/refresh";
const REFRESH_COOKIE_DAYS = 30;

// Single-flight: while a refresh is in-flight, concurrent 401s wait on the
// same promise. Without this, N parallel calls each fire their own /refresh,
// the first rotation invalidates the rest, and the family gets revoked.
let inFlight: Promise<boolean> | null = null;

export function getAccessToken(): string | null {
  return getCookie("accessToken");
}

export function getRefreshToken(): string | null {
  return getCookie("refreshToken");
}

export function setAuthCookies(args: {
  access_token: string;
  token_type: string;
  refresh_token?: string | null;
}): void {
  setCookie("accessToken", args.access_token, REFRESH_COOKIE_DAYS);
  setCookie("tokenType", args.token_type, REFRESH_COOKIE_DAYS);
  if (args.refresh_token) {
    setCookie("refreshToken", args.refresh_token, REFRESH_COOKIE_DAYS);
  }
}

export function clearAuth(): void {
  clearAllCookies();
}

// Tracks the access token we'd already seen before we started refreshing.
// If another tab rotates the cookie under us, the value changes — proof that
// someone else's refresh succeeded, so we can reuse their tokens instead of
// burning our own refresh and tripping reuse detection.
let accessTokenAtRefreshStart: string | null = null;

async function doRefresh(): Promise<boolean> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return false;

  // If a sibling tab already rotated since we last saw the access token,
  // adopt their result rather than calling /refresh ourselves.
  const currentAccess = getAccessToken();
  if (
    accessTokenAtRefreshStart !== null &&
    currentAccess !== null &&
    currentAccess !== accessTokenAtRefreshStart
  ) {
    return true;
  }

  const response = await fetch(`${BASE_URL}${REFRESH_PATH}`, {
    method: "POST",
    headers: {
      accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ refresh_token: refreshToken }),
  }).catch(() => null);

  if (!response || !response.ok) return false;

  try {
    const data = await response.json();
    setAuthCookies({
      access_token: data.access_token,
      token_type: data.token_type,
      refresh_token: data.refresh_token,
    });
    return true;
  } catch {
    return false;
  }
}

export function refreshAccessToken(): Promise<boolean> {
  if (inFlight) return inFlight;
  accessTokenAtRefreshStart = getAccessToken();
  inFlight = doRefresh().finally(() => {
    inFlight = null;
    accessTokenAtRefreshStart = null;
  });
  return inFlight;
}

// A token this close to expiry is refreshed before it is handed to a WebSocket.
const EXPIRY_MARGIN_SECONDS = 30;

function accessTokenExpiry(token: string): number | null {
  try {
    const payload = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    const { exp } = JSON.parse(atob(payload));
    return typeof exp === "number" ? exp : null;
  } catch {
    return null;
  }
}

/**
 * Refresh the access token if it has expired or is about to. A refused WebSocket
 * handshake gives the browser no 401 to react to, so a socket must check this
 * itself before (re)connecting, or it retries with a dead token forever.
 */
export async function ensureFreshAccessToken(): Promise<void> {
  const token = getAccessToken();
  if (!token) return;
  const expiry = accessTokenExpiry(token);
  if (expiry !== null && expiry - Date.now() / 1000 > EXPIRY_MARGIN_SECONDS) return;
  await refreshAccessToken();
}

/** Call backend /logout to revoke the refresh family, then clear local cookies. */
export async function logout(): Promise<void> {
  const accessToken = getCookie("accessToken");
  const tokenType = getCookie("tokenType");
  const refreshToken = getRefreshToken();
  if (accessToken && tokenType) {
    // Best-effort: don't block local logout on server errors / network.
    await fetch(`${BASE_URL}/logout`, {
      method: "POST",
      headers: {
        accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `${tokenType} ${accessToken}`,
      },
      body: JSON.stringify({ refresh_token: refreshToken }),
    }).catch(() => null);
  }
  clearAllCookies();
}
